import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import SOURCE_TYPE from '../SourceCreate.constants';
import {
  selectedSourceTypeIdAtom,
  sourceDialogModeAtom
} from '../SourceCreate.store';
import { SourceDialogMode } from '../SourceCreate.types';
import type {
  ChildProviderStates,
  PreviousState,
  StateChanges
} from './CloudSourceHooks.types';

/**
 * Data provider field paths in the form hierarchy.
 * These represent the four Microsoft sub-providers that can be individually toggled.
 */
const DATA_PROVIDER_PATHS = {
  O365: 'data_provider.O365.active',
  DEFENDER: 'data_provider.MicrosoftDefenderUnified.active',
  GRAPH_API: 'data_provider.MicrosoftEntraID.GraphAPI.active',
  O365_API: 'data_provider.MicrosoftEntraID.O365API.active'
} as const;

// ============================================================================
// PURE FUNCTIONS - Business Logic
// ============================================================================

/**
 * Checks if any child data provider is active.
 *
 * Business Rule: Parent should be active only if at least one child is active.
 *
 * @param childStates - Object containing all child provider active states
 * @returns True if any child provider is active
 */
const isAnyProviderActive = (childStates: ChildProviderStates): boolean => {
  return Object.values(childStates).some(Boolean);
};

/**
 * Detects what changed between previous and current state.
 *
 * This enables us to determine whether the user toggled:
 * - The parent checkbox (didParentChange && !didChildrenChange)
 * - A child checkbox (didChildrenChange)
 *
 * @param prevState - Previous state snapshot
 * @param currentParent - Current parent active state
 * @param currentChildren - Current child provider states
 * @returns Object indicating what changed
 */
const detectStateChanges = (
  prevState: PreviousState,
  currentParent: boolean,
  currentChildren: ChildProviderStates
): StateChanges => {
  const didChildrenChange =
    prevState.o365Active !== currentChildren.o365 ||
    prevState.defenderActive !== currentChildren.defender ||
    prevState.graphActive !== currentChildren.graph ||
    prevState.o365ApiActive !== currentChildren.o365Api;

  const didParentChange = prevState.rootActive !== currentParent;

  return { didParentChange, didChildrenChange };
};

/**
 * Determines if parent state change should cascade to children.
 *
 * Business Rule: When user toggles parent, all children should match parent state.
 * This is detected when parent changed but children didn't (user action).
 *
 * @param changes - Detected state changes
 * @returns True if children should be synced to parent
 */
const shouldSyncToChildren = (changes: StateChanges): boolean => {
  return changes.didParentChange && !changes.didChildrenChange;
};

/**
 * Determines if parent should be updated based on children.
 *
 * Business Rule: When user toggles a child, parent should reflect "any child active".
 *
 * @param changes - Detected state changes
 * @param currentParent - Current parent active state
 * @param isAnyChildActive - Whether any child is currently active
 * @returns True if parent needs to be updated
 */
const shouldUpdateParent = (
  changes: StateChanges,
  currentParent: boolean,
  isAnyChildActive: boolean
): boolean => {
  return changes.didChildrenChange && currentParent !== isAnyChildActive;
};

// ============================================================================
// CUSTOM HOOKS - Encapsulated Concerns
// ============================================================================

/**
 * Watches and memoizes all child provider active states.
 *
 * Encapsulates the concern of tracking multiple child provider states
 * and prevents stale closures in the main synchronization logic.
 *
 * @returns Memoized object containing all child provider states
 */
const useChildProviderStates = (): ChildProviderStates => {
  const { control } = useFormContext();

  const o365Active = useWatch({ control, name: DATA_PROVIDER_PATHS.O365 });
  const defenderActive = useWatch({
    control,
    name: DATA_PROVIDER_PATHS.DEFENDER
  });
  const graphActive = useWatch({
    control,
    name: DATA_PROVIDER_PATHS.GRAPH_API
  });
  const o365ApiActive = useWatch({
    control,
    name: DATA_PROVIDER_PATHS.O365_API
  });

  return useMemo(
    () => ({
      o365: o365Active,
      defender: defenderActive,
      graph: graphActive,
      o365Api: o365ApiActive
    }),
    [o365Active, defenderActive, graphActive, o365ApiActive]
  );
};

/**
 * Manages the synchronization lock to prevent feedback loops.
 *
 * When we programmatically update form values, React Hook Form triggers
 * watch callbacks. Without a lock, this creates infinite loops:
 * - Parent updates → Children update → Parent updates → ...
 *
 * The lock ref allows us to mark programmatic updates and skip processing them.
 *
 * @param mode - Current dialog mode
 * @returns Ref object for the update lock
 */
const useSyncLock = (mode: string): React.MutableRefObject<boolean> => {
  const isUpdatingRef = useRef(false);

  // Reset lock when dialog closes or mode changes
  useEffect(() => {
    if (mode !== SourceDialogMode.Edit) {
      isUpdatingRef.current = false;
    }
  }, [mode]);

  return isUpdatingRef;
};

/**
 * Synchronizes the active state between parent Microsoft source and child data providers.
 *
 * BUSINESS RULES:
 * 1. Parent "active" checkbox should be true ONLY if at least one child provider is active
 * 2. When parent is toggled ON: all child providers become active
 * 3. When parent is toggled OFF: all child providers become inactive
 * 4. When any child is toggled: parent syncs to "any child active" state
 *
 * COMPLEXITY NOTE:
 * This hook prevents feedback loops where parent updates trigger child updates
 * which would trigger parent updates again. We track previous state and use
 * a ref-based lock to detect user-initiated changes vs programmatic updates.
 *
 * APPLIES TO: Microsoft sources only, Edit mode only
 */
const useSourceDataProviderActiveState = () => {
  const { setValue, control } = useFormContext();
  const [selectedSourceTypeId] = useAtom(selectedSourceTypeIdAtom);
  const [mode] = useAtom(sourceDialogModeAtom);

  // Watch child provider states using custom hook
  const childProviderStates = useChildProviderStates();

  // Watch parent active state
  const parentActive = useWatch({ control, name: 'active' });

  // Watch selected states (tracked for state snapshot)
  const o365Selected = useWatch({
    control,
    name: 'data_provider.O365.selected'
  });
  const defenderSelected = useWatch({
    control,
    name: 'data_provider.MicrosoftDefenderUnified.selected'
  });
  const graphSelected = useWatch({
    control,
    name: 'data_provider.MicrosoftEntraID.GraphAPI.selected'
  });
  const o365ApiSelected = useWatch({
    control,
    name: 'data_provider.MicrosoftEntraID.O365API.selected'
  });

  // Previous state snapshot for change detection
  const prevState = useRef<PreviousState | null>(null);

  // Synchronization lock to prevent feedback loops
  const isUpdatingRef = useSyncLock(mode);

  // Reset refs when mode changes to prevent stale state
  useEffect(() => {
    if (mode !== SourceDialogMode.Edit) {
      prevState.current = null;
    }
  }, [mode]);

  useEffect(() => {
    // Only apply synchronization for Microsoft sources in Edit mode
    if (
      mode !== SourceDialogMode.Edit ||
      selectedSourceTypeId !== SOURCE_TYPE.MICROSOFT
    ) {
      return;
    }

    // Skip if we're in the middle of programmatic updates
    if (isUpdatingRef.current) {
      return;
    }

    // Calculate if any child is active
    const isAnyChildActive = isAnyProviderActive(childProviderStates);

    // First render - initialize parent to match children
    if (!prevState.current) {
      if (parentActive !== isAnyChildActive) {
        updateParentActiveState(isAnyChildActive, setValue, isUpdatingRef);
      }
      prevState.current = createStateSnapshot(
        parentActive,
        childProviderStates,
        { o365Selected, defenderSelected, graphSelected, o365ApiSelected }
      );
      return;
    }

    // Detect what changed since last render
    const changes = detectStateChanges(
      prevState.current,
      parentActive,
      childProviderStates
    );

    // Save current state BEFORE making any changes
    // This ensures we can detect the next change correctly
    prevState.current = createStateSnapshot(parentActive, childProviderStates, {
      o365Selected,
      defenderSelected,
      graphSelected,
      o365ApiSelected
    });

    // CASE 1: Parent changed but children didn't → User toggled parent checkbox
    // Action: Cascade parent state to all children
    if (shouldSyncToChildren(changes)) {
      syncChildrenToParent(parentActive, setValue, isUpdatingRef);
      return;
    }

    // CASE 2: One or more children changed → User toggled a child checkbox
    // Action: Update parent to reflect "any child active" state
    if (shouldUpdateParent(changes, parentActive, isAnyChildActive)) {
      updateParentActiveState(isAnyChildActive, setValue, isUpdatingRef);
    }
  }, [
    childProviderStates,
    parentActive,
    o365Selected,
    defenderSelected,
    graphSelected,
    o365ApiSelected,
    mode,
    selectedSourceTypeId,
    setValue,
    isUpdatingRef
  ]);
};

// ============================================================================
// HELPER FUNCTIONS - Form Updates
// ============================================================================

/**
 * Updates the parent "active" checkbox to match child provider states.
 * Uses a ref lock to prevent feedback loops.
 *
 * @param newActive - New active state for parent
 * @param setValue - React Hook Form setValue function
 * @param isUpdatingRef - Ref to track programmatic updates
 */
const updateParentActiveState = (
  newActive: boolean,
  setValue: ReturnType<typeof useFormContext>['setValue'],
  isUpdatingRef: React.MutableRefObject<boolean>
): void => {
  isUpdatingRef.current = true;
  setValue('active', newActive, {
    shouldDirty: true,
    shouldValidate: false
  });
  isUpdatingRef.current = false;
};

/**
 * Cascades the parent active state to all child data providers.
 * When parent is toggled, all children should match its state.
 *
 * @param parentState - Parent active state to cascade
 * @param setValue - React Hook Form setValue function
 * @param isUpdatingRef - Ref to track programmatic updates
 */
const syncChildrenToParent = (
  parentState: boolean,
  setValue: ReturnType<typeof useFormContext>['setValue'],
  isUpdatingRef: React.MutableRefObject<boolean>
): void => {
  isUpdatingRef.current = true;
  setValue(DATA_PROVIDER_PATHS.O365, parentState, { shouldDirty: true });
  setValue(DATA_PROVIDER_PATHS.DEFENDER, parentState, { shouldDirty: true });
  setValue(DATA_PROVIDER_PATHS.GRAPH_API, parentState, { shouldDirty: true });
  setValue(DATA_PROVIDER_PATHS.O365_API, parentState, { shouldDirty: true });
  isUpdatingRef.current = false;
};

/**
 * Creates a snapshot of current form state for change detection.
 * This allows us to determine what the user changed in the next render.
 *
 * @param rootActive - Parent active state
 * @param childStates - Child provider active states
 * @param selectedStates - Child provider selected states
 * @returns Complete state snapshot
 */
const createStateSnapshot = (
  rootActive: boolean,
  childStates: ChildProviderStates,
  selectedStates: {
    o365Selected: boolean;
    defenderSelected: boolean;
    graphSelected: boolean;
    o365ApiSelected: boolean;
  }
): PreviousState => {
  return {
    rootActive,
    o365Active: childStates.o365,
    defenderActive: childStates.defender,
    graphActive: childStates.graph,
    o365ApiActive: childStates.o365Api,
    o365Selected: selectedStates.o365Selected,
    defenderSelected: selectedStates.defenderSelected,
    graphSelected: selectedStates.graphSelected,
    o365ApiSelected: selectedStates.o365ApiSelected
  };
};

export default useSourceDataProviderActiveState;
